import { NotificationsBell } from '@/components/layout/NotificationsBell';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { CreateWorkspaceDialog } from '@/components/settings/CreateWorkspaceDialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLogout, useMe, useSwitchTenant } from '@/hooks/use-auth';
import { useCanRole } from '@/hooks/use-role';
import { useNavigate } from '@tanstack/react-router';
import {
  Building2,
  Check,
  ChevronDown,
  LogOut,
  Plus,
  Settings,
  User as UserIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  operator: 'Operator',
  viewer: 'Viewer',
};

export function Topbar() {
  const me = useMe();
  const logout = useLogout();
  const switchTenantMutation = useSwitchTenant();
  const navigate = useNavigate();
  const canAdmin = useCanRole('admin');
  const [createOpen, setCreateOpen] = useState(false);

  const user = me.data?.user;
  const memberships = me.data?.memberships ?? [];
  // Active tenant: vem do backend (`/me.currentTenant`) — reflete o estado real
  // de `sessions.currentTenantId`. UI nunca diverge do que requests veem.
  const active = me.data?.currentTenant ?? memberships[0] ?? null;
  const tenantName = active?.tenantName ?? 'Carregando...';
  const role = active ? ROLE_LABEL[active.role] : '';
  const hasMultipleTenants = memberships.length > 1;

  async function handleLogout(): Promise<void> {
    try {
      await logout.mutateAsync();
    } finally {
      void navigate({ to: '/login' });
    }
  }

  async function handleSwitchTenant(tenantId: string, name: string): Promise<void> {
    if (active?.tenantId === tenantId) return;
    try {
      await switchTenantMutation.mutateAsync(tenantId);
      toast.success(`Workspace ativa: ${name}`);
      // qc.clear() do useSwitchTenant já invalida tudo; próximo /me retorna
      // currentTenant atualizado e re-renderiza o switcher com o novo ativo.
    } catch {
      // onError do hook já mostra toast
    }
  }

  const initial = (user?.name?.[0] ?? user?.email?.[0] ?? '?').toUpperCase();
  const displayName = user?.name ?? user?.email?.split('@')[0] ?? '—';

  // Switcher é exibido como dropdown SEMPRE que canAdmin (pra ter espaço pro
  // botão "+ Nova workspace") — mesmo se só tem 1 workspace. Pra non-admin
  // sem múltiplos tenants, vira display estático sem dropdown (sem ações).
  const showSwitcher = hasMultipleTenants || canAdmin;

  return (
    <header className="flex h-14 items-center justify-between border-b bg-card px-6">
      {showSwitcher ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 gap-2 px-2 text-sm">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span>
                <span className="font-medium">{tenantName}</span>
                {role ? <span className="ml-2 text-xs text-muted-foreground">· {role}</span> : null}
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[28rem] w-72 overflow-y-auto">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground">Trocar workspace</span>
                <span className="text-[11px] text-muted-foreground/70">
                  {memberships.length}{' '}
                  {memberships.length === 1 ? 'workspace vinculado' : 'workspaces vinculados'}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {memberships.map((m) => {
              const isActive = m.tenantId === active?.tenantId;
              return (
                <DropdownMenuItem
                  key={m.tenantId}
                  onSelect={() => void handleSwitchTenant(m.tenantId, m.tenantName)}
                  disabled={switchTenantMutation.isPending}
                >
                  <div className="flex w-full items-start justify-between gap-2">
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-medium">{m.tenantName}</span>
                        {m.isPersonal ? (
                          <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                            Pessoal
                          </span>
                        ) : null}
                      </div>
                      <div className="text-[11px] text-muted-foreground">{ROLE_LABEL[m.role]}</div>
                    </div>
                    {isActive ? (
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-500" />
                    ) : null}
                  </div>
                </DropdownMenuItem>
              );
            })}
            {canAdmin ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => setCreateOpen(true)}
                  className="text-primary focus:text-primary"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Nova workspace
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Building2 className="h-4 w-4" />
          <span className="font-medium text-foreground">{tenantName}</span>
          {role ? <span className="ml-2 text-xs">· {role}</span> : null}
        </div>
      )}

      <CreateWorkspaceDialog open={createOpen} onOpenChange={setCreateOpen} />

      <div className="flex items-center gap-2">
        <ThemeToggle />
        <NotificationsBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 gap-2 px-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {initial}
              </span>
              <span className="hidden text-sm md:inline">{displayName}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col">
                <span className="text-sm font-medium">{user?.name ?? '—'}</span>
                <span className="text-xs text-muted-foreground">{user?.email ?? '—'}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void navigate({ to: '/account/profile' })}>
              <UserIcon className="mr-2 h-4 w-4" />
              Perfil
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void navigate({ to: '/account/security' })}>
              <Settings className="mr-2 h-4 w-4" />
              Segurança
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                void handleLogout();
              }}
              disabled={logout.isPending}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {logout.isPending ? 'Saindo...' : 'Sair'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
